import type { VersionConfig } from "../index";

/**
 * Mapa de celdas AZULES de MODELO WX FINAL2 (13 pestañas). El parser solo lee lo que está aquí;
 * nunca celdas grises. Fuente: diagnosticar_modelo.py + modelo-celda-por-celda.md + el volcado
 * de fórmulas (projects/wealth-x-12/fuentes/formulas-modelo-final2-fvt.txt).
 */
export const WX_FINAL2: VersionConfig = {
  id: "wx-final2",
  label: "MODELO WX FINAL2",
  catalogId: "wx-final2",
  detect: {
    requiredSheets: [
      "Índice y setup", "0 · Diagnóstico", "Mi Foto", "1 · Identidad", "2 · Vida", "3A · Flujo",
      "3B · Balance", "4 · Estrategia", "4B · Mi Portafolio", "5 · Sistema", "Catálogo",
    ],
    signature: { sheet: "Índice y setup", cell: "A2", includes: "Diseño Patrimonial en 5 Pasos" },
  },
  sheets: {
    setup: "Índice y setup", diag: "0 · Diagnóstico", photo: "Mi Foto", identity: "1 · Identidad", life: "2 · Vida",
    flow: "3A · Flujo", balance: "3B · Balance", strategy: "4 · Estrategia", portfolio: "4B · Mi Portafolio",
    system: "5 · Sistema", portfoliosModel: "Portafolios modelo", assetsCatalog: "Catálogo de activos", catalog: "Catálogo",
  },
  cells: {
    setup: { age: "B7", year: "B8", targetAge: "B9", currency: "B10" }, // B6 ("Tu nombre") no se lee desde el 2-nov-2026
    // Del evento (E63:E67 domingo, F96/F103/F110 rueda del domingo, B73/B75 reflexiones) ya no se lee: el diagnóstico se contesta en la app.
    diag: { s3: "E14:E17", s4: "E21:E24", s5: "E28:E31", s1Avg: "E45", s2Avg: "E54", wheelAvg: ["E96", "E103", "E110"] },
    identity: {
      story: ["A6", "A8", "A10", "A12", "A14"], values: ["B18", "B19", "B20"],
      needs: { rows: [25, 30], cols: { need: "A", me: "B", partner: "C", child1: "D", child2: "E" } },
      archetype: "B40", archetype2: "B41", trap: "A43",
    },
    life: {
      purpose: ["A6", "A8", "A10", "A12", "A14", "A16"],
      film: { rows: [20, 23], cols: { phase: "A", from: "B", to: "C", source: "D", goals: "E" } },
      emergencyMonths: "C28",
      objectives: { rows: [29, 37], cols: { priority: "A", name: "B", age: "C", type: "E", costToday: "F", strategyOverride: "J" } },
      emergencyStrategyOverride: "J28",
      freeCapitalStrategy: "F44",
    },
    flow: {
      incomes: { rows: [6, 15], cols: { source: "A", amount: "B", modality: "C" } },
      expenses: { rows: [24, 53], cols: { concept: "A", category: "B", amount: "C", modality: "D" } },
      reflections: ["A85", "A87", "A89"],
    },
    balance: {
      assets: { rows: [6, 45], cols: { what: "A", category: "B", subcategory: "C", value: "D", bucketOverride: "J" } },
      liabilities: { rows: [51, 62], cols: { what: "A", balance: "B", payment: "C", rate: "D", personalUse: "E" } },
    },
    strategy: { concreteAssets: "H6:H15", misalignment: "A40", closeIn: "A43", manualRealRate: "C56", manualContribution: "C61" },
    portfolio: {
      capitalOverride: "C6",
      buckets: { pres: [15, 20], inc: [26, 33], growth: [39, 46], projects: [52, 54], contrib: [60, 61] },
      cols: { category: "A", subcategory: "B", pct: "C" },
    },
    system: {
      team: { rows: [6, 14], cols: { role: "A", has: "B", name: "C", satisfaction: "D", cadence: "E", need: "F" } },
      cadence: { rows: [21, 26], cols: { what: "A", every: "B", who: "C", next: "D" } },
      filter: { rows: [33, 43], cols: { opportunity: "A", bucket: "B", objective: "C", offered: "D" } },
      plan30: { rows: [46, 48], cols: { action: "B", step: "C", deadline: "D", who: "E" } },
      courage: { act: "A53", area: "B53", when: "C53" },
    },
  },
  rules: { cascadeBase: "gross", portfolioBase: "net", excelProjectionRows: 35 },
  // Rótulos literales de la plantilla (iguales en FINAL, FINAL2 y NATION BASE). Solo los que anclan una celda o cierran una tabla:
  // cada uno que falte en el archivo del miembro genera un aviso, así que no se listan títulos decorativos.
  landmarks: {
    setup: [
      { id: "nombre", cell: "A6", text: "Tu nombre" }, { id: "edad", cell: "A7", text: "Edad actual" }, { id: "anio", cell: "A8", text: "Año actual" },
      { id: "edadMeta", cell: "A9", text: "Edad hasta la que quieres proyectar" }, { id: "moneda", cell: "A10", text: "Moneda en la que capturas", prefix: true },
    ],
    diag: [
      { id: "paso3", cell: "A13", text: "PASO 3 · MI REALIDAD", prefix: true }, { id: "paso4", cell: "A20", text: "PASO 4 · MI ESTRATEGIA", prefix: true },
      { id: "paso5", cell: "A27", text: "PASO 5 · MI SISTEMA", prefix: true }, { id: "paso1", cell: "A38", text: "PASO 1 · MI IDENTIDAD", prefix: true },
      { id: "paso2", cell: "A47", text: "PASO 2 · MI VIDA", prefix: true }, { id: "rueda", cell: "A88", text: "④ MI RUEDA DE RIQUEZA 360", prefix: true },
    ],
    identity: [
      { id: "p1", cell: "A5", text: "¿Cómo fue tu vida", prefix: true }, { id: "p2", cell: "A7", text: "¿Qué marcas y creencias", prefix: true },
      { id: "p3", cell: "A9", text: "¿Qué hábitos con el dinero", prefix: true }, { id: "p4", cell: "A11", text: "¿Cómo te relacionas con el dinero", prefix: true },
      { id: "p5", cell: "A13", text: "Si pudieras definir una pregunta", prefix: true },
      { id: "valores", cell: "A17", text: "MIS 3 VALORES INNEGOCIABLES" }, { id: "necesidad", cell: "A24", text: "Necesidad" },
      { id: "arquetipo", cell: "A40", text: "Mi arquetipo dominante" }, { id: "arquetipo2", cell: "A41", text: "Mi segundo arquetipo" },
      { id: "trampa", cell: "A42", text: "¿Dónde me mete el pie", prefix: true },
    ],
    life: [
      { id: "q1", cell: "A5", text: "¿Quiénes somos como familia", prefix: true }, { id: "q2", cell: "A7", text: "¿Cuáles son nuestros valores", prefix: true },
      { id: "q3", cell: "A9", text: "¿Cuál es el verdadero propósito", prefix: true }, { id: "q4", cell: "A11", text: "¿Cómo queremos que nos recuerden", prefix: true },
      { id: "q5", cell: "A13", text: "¿Cómo tomamos las decisiones de dinero", prefix: true }, { id: "declaracion", cell: "A15", text: "MI DECLARACIÓN DE PROPÓSITO", prefix: true },
      { id: "fases", cell: "A19", text: "Fase de mi Vida" },
      { id: "prioridad", cell: "A27", text: "Prioridad" }, { id: "fondo", cell: "A28", text: "Fija" }, { id: "totalObjetivos", cell: "B38", text: "TOTAL", last: true },
      { id: "capitalLibre", cell: "B44", text: "Estrategia del capital libre" },
    ],
    flow: [
      { id: "fuente", cell: "A5", text: "Fuente" }, { id: "totalIngresos", cell: "A16", text: "TOTAL INGRESOS ANUALES" },
      { id: "concepto", cell: "A23", text: "Concepto" }, { id: "totalGastos", cell: "A54", text: "TOTAL GASTOS ANUALES" },
      { id: "r1", cell: "A84", text: "Lo que me sorprendió", prefix: true }, { id: "r2", cell: "A86", text: "El gasto que no está alineado", prefix: true },
      { id: "r3", cell: "A88", text: "Lo primero que voy a cambiar", prefix: true },
    ],
    balance: [
      { id: "queEs", cell: "A5", text: "¿Qué es?" }, { id: "totalActivos", cell: "B46", text: "TOTAL ACTIVOS", last: true },
      { id: "queDebo", cell: "A50", text: "¿Qué debo?" }, { id: "totalPasivos", cell: "A63", text: "TOTAL PASIVOS" },
    ],
    strategy: [
      { id: "objetivo", cell: "A5", text: "Objetivo" }, { id: "allocation", cell: "A18", text: "MI ALLOCATION", prefix: true },
      { id: "desalineacion", cell: "A39", text: "¿Cuál es mi desalineación", prefix: true },
      { id: "cerrarla", cell: "A42", text: "¿En cuánto tiempo", prefix: true }, { id: "tasaReal", cell: "A56", text: "Tasa real que quiero usar", prefix: true },
      { id: "ajuste", cell: "A61", text: "Ajuste manual", prefix: true },
    ],
    portfolio: [
      { id: "capital", cell: "A6", text: "Capital a distribuir", prefix: true },
      { id: "pres", cell: "A12", text: "🛡 PRESERVACIÓN" }, { id: "presFin", cell: "A21", text: "SUBTOTAL", unique: false },
      { id: "inc", cell: "A23", text: "💰 INGRESO" }, { id: "incFin", cell: "A34", text: "SUBTOTAL", unique: false },
      { id: "growth", cell: "A36", text: "📈 CRECIMIENTO" }, { id: "growthFin", cell: "A47", text: "SUBTOTAL", unique: false },
      { id: "projects", cell: "A49", text: "🎯 PROYECTOS" }, { id: "projectsFin", cell: "A55", text: "SUBTOTAL", unique: false },
      { id: "contrib", cell: "A57", text: "❤️ CONTRIBUCIÓN" }, { id: "contribFin", cell: "A62", text: "SUBTOTAL", unique: false },
      { id: "consolidado", cell: "A65", text: "MI PORTAFOLIO CONSOLIDADO" },
    ],
    system: [
      { id: "rol", cell: "A5", text: "Rol" }, { id: "rolesFaltan", cell: "A15", text: "Roles que me faltan" },
      { id: "queReviso", cell: "A20", text: "¿Qué reviso?" }, { id: "oportunidad", cell: "A32", text: "Oportunidad" },
      { id: "plan30", cell: "A44", text: "MI PLAN DE 30 DÍAS" }, { id: "numeral", cell: "A45", text: "#" },
      { id: "coraje", cell: "A52", text: "Mi acto de coraje" },
    ],
  },
  elastic: {
    assets: "totalActivos", liabilities: "totalPasivos", objectives: "totalObjetivos", incomes: "totalIngresos", expenses: "totalGastos",
    team: "rolesFaltan", filter: "plan30", pres: "presFin", inc: "incFin", growth: "growthFin", projects: "projectsFin", contrib: "contribFin",
  },
};
